import React, { Suspense, lazy } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { usePermissions } from '@/hooks/usePermissions'
import type { Permission } from '@/lib/rbac'
import AppShell from '@/components/layout/AppShell'
import { Spinner } from '@/components/ui'
import { getPendingHoursCount } from '@/lib/api/volunteers'
import { getInquiryStats } from '@/lib/api/inquiries'

// Public
import PublicLandingPage from '@/pages/PublicLandingPage'
import ResourceDirectoryPage from '@/pages/ResourceDirectoryPage'
import VolunteerRegistrationPage from '@/pages/VolunteerRegistrationPage'
import DAREPage from '@/pages/DAREPage'
import AgencyPickupPage from '@/pages/AgencyPickupPage'

// Staff pages — lazy loaded
const LoginPage          = lazy(() => import('@/pages/LoginPage'))
const PartnerLoginPage   = lazy(() => import('@/pages/PartnerLoginPage'))
const DashboardPage      = lazy(() => import('@/pages/DashboardPage'))
const WorkspaceLandingPage = lazy(() => import('@/pages/WorkspaceLandingPage'))
const WorkQueuePage       = lazy(() => import('@/pages/WorkQueuePage'))
const CasesPage          = lazy(() => import('@/pages/CasesPage'))
const VolunteersPage     = lazy(() => import('@/pages/VolunteersPage'))
const InventoryPage      = lazy(() => import('@/pages/InventoryPage'))
const DonorsPage         = lazy(() => import('@/pages/DonorsPage'))
const PartnersPage       = lazy(() => import('@/pages/PartnersPage'))
const StaffPage          = lazy(() => import('@/pages/StaffPage'))
const ReportsPage        = lazy(() => import('@/pages/ReportsPage'))
const AuditLogPage       = lazy(() => import('@/pages/AuditLogPage'))
const SettingsPage       = lazy(() => import('@/pages/SettingsPage'))
const ProfilePage        = lazy(() => import('@/pages/ProfilePage'))
const InquiriesPage      = lazy(() => import('@/pages/InquiriesPage'))
const StabilizationPage = lazy(() => import('@/pages/StabilizationPage'))
const StaffIntakePage = lazy(() => import('@/pages/StaffIntakePage'))
const AppointmentsPage   = lazy(() => import('@/pages/AppointmentsPage'))
const PartnerPortalPage    = lazy(() => import('@/pages/PartnerPortalPage'))
const VolunteerPortalPage  = lazy(() => import('@/pages/VolunteerPortalPage'))

const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-64" role="status" aria-label="Loading">
    <Spinner size="lg" className="text-brand-500" />
  </div>
)

// Reset scroll position on SPA navigation so links such as “Back to Guided Access”
// return users to the top of the destination page instead of preserving the old
// D.A.RE. page scroll position.
const ScrollToTop: React.FC = () => {
  const location = useLocation()
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    document.getElementById('main-content')?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname, location.search])
  return null
}


// ── Role → default landing page ───────────────────────────────
function getRoleHome(role: string, isAdmin: boolean): string {
  if (isAdmin) return '/dashboard'
  switch (role) {
    case 'executive_director':
    case 'operations_manager':    return '/dashboard'
    case 'program_manager':
    case 'staff':
    case 'employee':              return '/dashboard'
    case 'volunteer_coordinator': return '/volunteers'
    case 'volunteer':             return '/volunteer-portal'
    case 'partner_agency':        return '/partner-portal'
    default:                      return '/dashboard'
  }
}

// ── Protected layout ──────────────────────────────────────────
const ProtectedLayout: React.FC = () => {
  const { session, loading, profile, isAdmin } = useAuth()
  const location = useLocation()

  const { data: pendingHours = 0 } = useQuery({
    queryKey: ['pending-hours-count'],
    queryFn:  getPendingHoursCount,
    refetchInterval: 30_000,
    enabled: !!session && profile?.role !== 'partner_agency' && profile?.role !== 'volunteer',
  })

  const { data: inquiryStats } = useQuery({
    queryKey: ['inquiries', 'stats'],        // same key as InquiriesPage → shared cache
    queryFn:  getInquiryStats,
    refetchInterval: 30_000,
    enabled: !!session && profile?.role !== 'partner_agency' && profile?.role !== 'volunteer',
  })
  const newInquiries = inquiryStats?.new ?? 0

  if (loading)   return <PageLoader />
  if (!session)  return <Navigate to="/login" state={{ from: location.pathname }} replace />
  if (!profile)  return <PageLoader />

  // Partner agency accounts are isolated to their own portal/profile.
  // They must never inherit the staff dashboard, work queue, cases, appointments,
  // stabilization, or administration surfaces even if they know a direct URL.
  if (profile.role === 'partner_agency' && !['/partner-portal', '/profile'].includes(location.pathname)) {
    return <Navigate to="/partner-portal" replace />
  }

  // Volunteers use the volunteer portal only. Do not expose the staff dashboard,
  // appointment/work queues, or case-management routes through direct URLs.
  if (profile.role === 'volunteer' && !['/volunteer-portal', '/profile'].includes(location.pathname)) {
    return <Navigate to="/volunteer-portal" replace />
  }

  // Client/donor logins must not fall through to staff operations pages.
  // They may still open their own profile; public intake and resources remain public.
  const staffWorkspaceRoles = ['executive_director', 'operations_manager', 'program_manager', 'staff', 'volunteer_coordinator', 'employee']
  if (!staffWorkspaceRoles.includes(profile.role) && !['volunteer', 'partner_agency'].includes(profile.role) && location.pathname !== '/profile') {
    return <Navigate to="/" replace />
  }

  if (!profile.is_active) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Account Inactive</p>
          <p className="text-sm text-gray-500 mt-1">Contact your administrator to activate your account.</p>
          <a href="mailto:info@womenempoweringeachother.org" className="text-xs text-brand-500 hover:underline mt-3 block">
            info@weecarediaperbank.org
          </a>
        </div>
      </div>
    )
  }

  return (
    <AppShell pendingHours={pendingHours} newInquiries={newInquiries}>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </AppShell>
  )
}

// ── Role guards ───────────────────────────────────────────────
const AdminRoute: React.FC = () => {
  const { isAdmin } = useAuth()
  return isAdmin ? <Outlet /> : <Navigate to="/dashboard" replace />
}

const PartnerRoute: React.FC = () => {
  const { profile } = useAuth()
  return profile?.role === 'partner_agency' ? <Outlet /> : <Navigate to="/home" replace />
}

const StaffRoute: React.FC = () => {
  const { isStaff, profile } = useAuth()
  // Partner agency role should not access staff pages
  if (profile?.role === 'partner_agency') return <Navigate to="/partner-portal" replace />
  return isStaff ? <Outlet /> : <Navigate to="/dashboard" replace />
}

const PermissionRoute: React.FC<{ permission: Permission }> = ({ permission }) => {
  const { can } = usePermissions()
  return can(permission) ? <Outlet /> : <Navigate to="/dashboard" replace />
}

// ── RoleHome — redirects to role-appropriate landing page ─────
const RoleHome: React.FC = () => {
  const { profile, isAdmin } = useAuth()
  if (!profile) return <PageLoader />
  return <Navigate to={getRoleHome(profile.role, isAdmin)} replace />
}

// ── Router ────────────────────────────────────────────────────
const AppRouter: React.FC = () => (
  <>
    <ScrollToTop />
    <Routes>
    {/* Public */}
    <Route path="/" element={<PublicLandingPage />} />
    <Route path="/resources" element={<ResourceDirectoryPage />} />
    <Route path="/volunteer-registration" element={<VolunteerRegistrationPage />} />
    <Route path="/dare" element={<DAREPage />} />
    <Route path="/agency-pickup" element={<AgencyPickupPage />} />
    <Route path="/community" element={<Navigate to="/" replace />} />
    <Route path="/login"         element={<Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
    <Route path="/partner-login" element={<Suspense fallback={<PageLoader />}><PartnerLoginPage /></Suspense>} />

    {/* Protected */}
    <Route element={<ProtectedLayout />}>
      {/* Role-appropriate home */}
      <Route path="/home" element={<RoleHome />} />

      {/* Universal staff pages */}
      <Route path="/dashboard"     element={<DashboardPage />} />
      <Route path="/work-queue"   element={<WorkQueuePage />} />
      <Route path="/profile"       element={<ProfilePage />} />
      <Route path="/appointments"  element={<AppointmentsPage />} />
      <Route element={<PartnerRoute />}><Route path="/partner-portal" element={<PartnerPortalPage />} /></Route>
      <Route path="/volunteer-portal" element={<VolunteerPortalPage />} />

      {/* Staff + above */}
      <Route element={<StaffRoute />}>
        <Route path="/workspace/clients" element={<WorkspaceLandingPage />} />
        <Route path="/workspace/partners" element={<WorkspaceLandingPage />} />
        <Route path="/workspace/volunteers" element={<WorkspaceLandingPage />} />
        <Route path="/workspace/operations" element={<WorkspaceLandingPage />} />
        <Route path="/cases"       element={<CasesPage />} />
        <Route path="/cases/:id"   element={<CasesPage />} />
        <Route element={<PermissionRoute permission="volunteers.view" />}>
          <Route path="/volunteers" element={<VolunteersPage />} />
        </Route>
        <Route element={<PermissionRoute permission="inventory.view" />}>
          <Route path="/inventory" element={<InventoryPage />} />
        </Route>
        <Route element={<PermissionRoute permission="partners.view" />}>
          <Route path="/partners" element={<PartnersPage />} />
        </Route>
        <Route element={<PermissionRoute permission="nav.inquiries" />}>
          <Route path="/inquiries" element={<InquiriesPage />} />
        </Route>
        <Route path="/stabilization" element={<StabilizationPage />} />
        <Route path="/stabilization/walk-in" element={<StaffIntakePage />} />
      </Route>

      {/* Admin only */}
      <Route element={<AdminRoute />}>
        <Route path="/donors"      element={<DonorsPage />} />
        <Route path="/staff"       element={<StaffPage />} />
        <Route path="/audit"       element={<AuditLogPage />} />
        <Route path="/settings"    element={<SettingsPage />} />
        <Route path="/reports"     element={<ReportsPage />} />
      </Route>
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </>
)

export default AppRouter
